import React, { useEffect } from "react";
import _ from "lodash";
import { observer, useLocalStore } from "mobx-react";
import Modal from "@app/components/modal";
import notify from "@app/components/notify";
import { Input, Switch, DatePicker, InputNumber, Button, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import format from "@app/lib/format";
import moment from "moment";

import "./style/stage-modal.scoped.scss";

const StageEscalationModal = observer(({ visible, stage, onSave, onCancel }) => {
    const state = useLocalStore(() => ({
        title: "",
        escalationEnabled: false,
        deadline: null,
        escalationTriggers: [],
    }));

    useEffect(() => {
        if (!visible) {
            state.title = "";
            state.escalationEnabled = false;
            state.deadline = null;
            state.escalationTriggers = [];
        } else {
            state.title = stage?.title || "";
            state.escalationEnabled = stage?.escalationEnabled || false;
            state.deadline = stage?.deadline ? moment(stage.deadline) : null;
            state.escalationTriggers = _.cloneDeep(stage?.escalationTriggers ?? []);
        }
    }, [visible, stage]);

    const addEscalationTrigger = () => {
        const trigger = {
            _id: `temp_${Date.now()}`,
            days: 3,
        };

        resetTrigger(trigger);
        state.escalationTriggers.push(trigger);
    };

    const removeEscalationTrigger = (index) => {
        state.escalationTriggers.splice(index, 1);
    };

    const updateTrigger = (index, value) => {
        const trigger = state.escalationTriggers[index];
        trigger.days = value;

        resetTrigger(trigger);
    };

    const onOk = async () => {
        if (state.title?.trim() === "") {
            notify.error("Please enter a title");
            return;
        }

        if (state.escalationEnabled) {
            if (!state.deadline) {
                notify.error("Please set a review deadline when escalation is enabled");
                return;
            }

            if (state.escalationTriggers.length === 0) {
                notify.error("Please add at least one escalation trigger");
                return;
            }

            // Validate triggers
            for (const trigger of state.escalationTriggers) {
                if (!trigger.days || trigger.days < 1) {
                    notify.error("Escalation trigger days must be at least 1");
                    return;
                }
            }
        } else {
            state.deadline = null;
            state.escalationTriggers = [];
        }

        const escalationMap = {};
        const updatedStage = {
            ...stage,
            title: state.title,
            escalationEnabled: state.escalationEnabled,
            deadline: state.deadline ? state.deadline.toDate() : null,
            escalationTriggers: state.escalationTriggers
                ?.sort((a, b) => b.days - a.days)
                .filter((trigger) => {
                    if (escalationMap[String(trigger.days)]) {
                        return false;
                    }
                    escalationMap[String(trigger.days)] = true;
                    return true;
                })
                .map((trigger) => ({
                    ...trigger,
                    _id: trigger._id?.startsWith("temp_") ? undefined : trigger._id,
                })),
        };

        onSave && onSave(updatedStage);
    };

    const changeDeadline = (date) => {
        state.deadline = date;
        state.escalationTriggers.map((trigger) => resetTrigger(trigger));
    };

    const resetTrigger = (trigger) => {
        trigger.triggeredOn = null;

        if (trigger.days && state.deadline) {
            const days = Number(trigger.days);
            let triggerDate = state.deadline.clone();
            let wday;

            for (let i = 0; i < days; i++) {
                do {
                    triggerDate.subtract(1, "day");
                    wday = triggerDate.day();
                } while (wday === 6 || wday === 0);
            }

            trigger.triggerDate = triggerDate.toDate();
        }

        state.escalationTriggers = [...state.escalationTriggers];
    };

    return (
        visible && (
            <Modal
                title={"Configure Review Stage"}
                visible={true}
                onOk={onOk}
                onCancel={onCancel}
                width={700}
            >
                <div className="review-stage">
                    <div className="form-group">
                        <label>Stage Title *</label>
                        <Input
                            placeholder="Enter stage title"
                            value={state.title}
                            onChange={(e) => {
                                state.title = e.currentTarget.value;
                            }}
                        />
                    </div>

                    <Divider />

                    <div className="form-group">
                        <div className="switch-group">
                            <Switch
                                checked={state.escalationEnabled}
                                onChange={(checked) => {
                                    state.escalationEnabled = checked;
                                }}
                            />
                            <label>Configure Escalation</label>
                        </div>
                        <p className="help-text">
                            Enable automated escalation reminders for pending reviews
                        </p>
                    </div>

                    {state.escalationEnabled && (
                        <>
                            <div className="form-group">
                                <label>Review Deadline *</label>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    value={state.deadline}
                                    onChange={changeDeadline}
                                    placeholder="Set the deadline for review to be completed"
                                    disabledDate={(current) => {
                                        return current && current < moment().startOf("day");
                                    }}
                                />
                                <p className="help-text">
                                    Set the deadline for this review to be completed
                                </p>
                            </div>

                            <div className="form-group">
                                <label>Escalation Triggers</label>
                                <p className="help-text">
                                    Configure when escalation emails should be sent to reviewers
                                </p>

                                {state.escalationTriggers.map((trigger, index) => (
                                    <div key={trigger._id} className="escalation-trigger">
                                        <InputNumber
                                            min={1}
                                            max={365}
                                            value={trigger.days}
                                            onChange={(value) => updateTrigger(index, value)}
                                            placeholder="Days"
                                        />

                                        <div className="trigger-date">
                                            days before deadline{" "}
                                            <i>({format.date(trigger.triggerDate)})</i>
                                        </div>

                                        <div className="button">
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => removeEscalationTrigger(index)}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <Button
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={addEscalationTrigger}
                                    style={{ marginTop: 10, width: "100%" }}
                                >
                                    Add Escalation Trigger
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        )
    );
});

export default StageEscalationModal;
